import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiRequest } from '../../utils/apiUtils';
import { Venue, VenueType } from '../../types/venue';

export interface VenueFormData {
  // Basic Info
  name: string;
  description: string;
  venueType: VenueType;
  capacity: number;
  complexId?: string | null;

  // Location
  address: string;
  city: string;
  province: string;
  mapsUrl: string;

  // Amenities
  isIndoor: boolean;
  hasLighting: boolean;
  surfaceType: string;
  hasBathrooms: boolean;
  hasParking: boolean;
  hasChangingRooms: boolean;

  // Pricing
  hourlyRate: number;
  peakHourRate: number;
  depositAmount: number;
}

export interface VenueFormErrors {
  [key: string]: string;
}

const INITIAL_FORM_DATA: VenueFormData = {
  name: '',
  description: '',
  venueType: 'FUTBOL_5',
  capacity: 10,
  complexId: null,
  address: '',
  city: '',
  province: '',
  mapsUrl: '',
  isIndoor: false,
  hasLighting: false,
  surfaceType: 'Césped sintético',
  hasBathrooms: false,
  hasParking: false,
  hasChangingRooms: false,
  hourlyRate: 0,
  peakHourRate: 0,
  depositAmount: 0,
};

export const useVenueForm = (existingVenue?: Venue) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<VenueFormData>(
    existingVenue
      ? {
          name: existingVenue.name,
          description: existingVenue.description || '',
          venueType: existingVenue.venueType,
          capacity: existingVenue.capacity,
          complexId: existingVenue.complexId || null,
          address: existingVenue.address,
          city: existingVenue.city,
          province: existingVenue.province,
          mapsUrl: existingVenue.mapsUrl,
          isIndoor: existingVenue.isIndoor,
          hasLighting: existingVenue.hasLighting,
          surfaceType: existingVenue.surfaceType,
          hasBathrooms: existingVenue.hasBathrooms,
          hasParking: existingVenue.hasParking,
          hasChangingRooms: existingVenue.hasChangingRooms,
          hourlyRate: existingVenue.hourlyRate,
          peakHourRate: existingVenue.peakHourRate || 0,
          depositAmount: existingVenue.depositAmount,
        }
      : INITIAL_FORM_DATA
  );
  const [errors, setErrors] = useState<VenueFormErrors>({});
  const [loading, setLoading] = useState(false);

  // Update form when existingVenue changes (for edit mode)
  useEffect(() => {
    if (existingVenue) {
      setFormData({
        name: existingVenue.name,
        description: existingVenue.description || '',
        venueType: existingVenue.venueType,
        capacity: existingVenue.capacity,
        complexId: existingVenue.complexId || null,
        address: existingVenue.address,
        city: existingVenue.city,
        province: existingVenue.province,
        mapsUrl: existingVenue.mapsUrl,
        isIndoor: existingVenue.isIndoor,
        hasLighting: existingVenue.hasLighting,
        surfaceType: existingVenue.surfaceType,
        hasBathrooms: existingVenue.hasBathrooms,
        hasParking: existingVenue.hasParking,
        hasChangingRooms: existingVenue.hasChangingRooms,
        hourlyRate: existingVenue.hourlyRate,
        peakHourRate: existingVenue.peakHourRate || 0,
        depositAmount: existingVenue.depositAmount,
      });
    }
  }, [existingVenue]);

  const getCapacityFromType = (type: VenueType): number => {
    const match = type.match(/FUTBOL_(\d+)/);
    return match ? parseInt(match[1]) * 2 : 10;
  };

  const updateField = useCallback(
    <K extends keyof VenueFormData>(field: K, value: VenueFormData[K]) => {
      setFormData((prev) => {
        const updated = { ...prev, [field]: value };
        // Auto-calculate capacity from venueType
        if (field === 'venueType') {
          updated.capacity = getCapacityFromType(value as VenueType);
        }
        return updated;
      });
      // Clear error for this field when user starts typing
      if (errors[field]) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      }
    },
    [errors]
  );

  const validateForm = useCallback((): boolean => {
    // eslint-disable-next-line no-console
    console.log('🔍 [useVenueForm] Validando formulario...');
    // eslint-disable-next-line no-console
    console.log('📋 [useVenueForm] Form data:', formData);

    const newErrors: VenueFormErrors = {};

    // Basic Info
    if (!formData.name.trim()) {
      newErrors.name = 'El nombre es requerido';
    } else if (formData.name.length < 3) {
      newErrors.name = 'El nombre debe tener al menos 3 caracteres';
    }

    // Location is inherited from complex, no validation needed

    if (!formData.surfaceType.trim()) {
      newErrors.surfaceType = 'El tipo de superficie es requerido';
    }

    // Pricing
    if (formData.hourlyRate <= 0) {
      newErrors.hourlyRate = 'El precio por hora debe ser mayor a 0';
    }

    if (formData.peakHourRate < 0) {
      newErrors.peakHourRate = 'El precio en hora pico no puede ser negativo';
    }

    if (
      formData.peakHourRate > 0 &&
      formData.peakHourRate <= formData.hourlyRate
    ) {
      newErrors.peakHourRate =
        'El precio en hora pico debe ser mayor al precio normal';
    }

    if (formData.depositAmount < 0) {
      newErrors.depositAmount = 'El depósito no puede ser negativo';
    }

    setErrors(newErrors);
    const isValid = Object.keys(newErrors).length === 0;

    if (!isValid) {
      // eslint-disable-next-line no-console
      console.error('❌ [useVenueForm] Validación fallida:', newErrors);
    } else {
      // eslint-disable-next-line no-console
      console.log('✅ [useVenueForm] Validación exitosa');
    }

    return isValid;
  }, [formData]);

  const submitForm = useCallback(async () => {
    // eslint-disable-next-line no-console
    console.log('🚀 [useVenueForm] Iniciando submit...');

    if (!validateForm()) {
      // eslint-disable-next-line no-console
      console.error('❌ [useVenueForm] Validación fallida, abortando submit');
      return { success: false, error: 'Por favor completá todos los campos requeridos' };
    }

    setLoading(true);
    // eslint-disable-next-line no-console
    console.log('⏳ [useVenueForm] Loading activado');

    try {
      const endpoint = existingVenue
        ? `/venues/${existingVenue.id}`
        : '/venues';
      const method = existingVenue ? 'PUT' : 'POST';

      // eslint-disable-next-line no-console
      console.log(`📡 [useVenueForm] Enviando ${method} a ${endpoint}`);
      // eslint-disable-next-line no-console
      console.log('📦 [useVenueForm] Body:', formData);

      // Location is inherited from complex on backend, strip location fields
      const { address, city, province, mapsUrl, ...submitData } = formData;

      const response = await apiRequest<Venue>(endpoint, {
        method,
        body: submitData,
      });

      // eslint-disable-next-line no-console
      console.log('📨 [useVenueForm] Respuesta recibida:', response);

      if (response.data) {
        setLoading(false);
        // eslint-disable-next-line no-console
        console.log('✅ [useVenueForm] Venue guardado exitosamente');
        return { success: true, venue: response.data };
      } else {
        setLoading(false);
        // eslint-disable-next-line no-console
        console.error('❌ [useVenueForm] Error en respuesta:', response.error);
        return { success: false, error: response.error || 'Error al guardar el venue' };
      }
    } catch (error) {
      setLoading(false);
      // eslint-disable-next-line no-console
      console.error('💥 [useVenueForm] Exception capturada:', error);
      return { success: false, error: 'Error de conexión' };
    }
  }, [formData, existingVenue, validateForm]);

  const resetForm = useCallback(() => {
    setFormData(INITIAL_FORM_DATA);
    setErrors({});
  }, []);

  return {
    formData,
    errors,
    loading,
    updateField,
    validateForm,
    submitForm,
    resetForm,
  };
};
